// 处理用户在游戏内的表单输入
// 提供修改表单的公共方法

import { useJoinStateStore } from '~/stores/cansino/joinState';
import useSettingsStore from '@/stores/settings';
import { useUserStore } from '~/stores/user';
import betHelper from '../betHelper';
import { useAutoOptionStore } from '~/stores/cansino/autoOption';
import { getGameName } from '@/common/js/gameAbout';
import { useAmountMemoryStore } from '~/stores/cansino/amountMemory';


// 标准的表单对象
// ▼ 多个游戏“通用”的属性才往里放，某个游戏专有的表单属性不放进来
export interface FormObject {
    amount: number; // 下注金额
    isSubmiting?: boolean; // 用于自动下注模式
    [key: string]: any;
}

// ▼取得一个标准表单对象（object)
// ▼推荐使用此方法创建表单对象以便得到良好的类型提示
export function createBettingForm(
    addition: object = {}, // 追加物（可选）
): FormObject {
    const oForm: FormObject = reactive({
        amount: 0,
        isSubmiting: false,
        ...addition,
    });
    return oForm;
}

const isDev = import.meta.dev;

// ▼ 用于各个游戏的表单操作（主要是设定金额）
export function useBettingToolkit(
    oForm: FormObject, // 表单对象
    oOptions: object = {}, // 配置项（预置在此，未必用得上）
) {
    // const { watchIncrease } = oOptions;
    const userStore = useUserStore(); // 查询用户余额
    const joinState = useJoinStateStore(); // 查询游戏的押注下限上限
    const settingStore = useSettingsStore(); // 查询当前币种

    // ▼ 表示余额是否足够下注，即余额 >= 最小下注金额
    const nowBlance = computed(() => {
        return userStore.oNowCurrency.balance || 0;
    });
    // ▼ 表示余额是否足够下注，即余额 >= 最小下注金额
    const isBalanceEnough = computed(() => {
        return nowBlance.value >= joinState.betttingLimits?.minBetAmount;
    });
    // ▼最终下注“下”限
    const minFinalValue = computed(() => {
        if (!isBalanceEnough.value) return 0; // 无下注资格
        const fResult = Math.max(
            0.01, // 输入框只能保留2位小数，所以这是最小值 
            joinState.betttingLimits?.minBetAmount,
        );
        return fResult;
    });
    // ▼最终下注“上”限
    const maxFinalValue = computed(() => {
        if (!isBalanceEnough.value) return 0; // 无下注资格
        return Math.min(nowBlance.value, joinState.betttingLimits?.maxBets);
    });
    // ▼ 特殊游戏 crash 上局下注信息
    const oCrashLastRound = computed(() => {
        const firstCheck = (
            joinState.gameName.includes('crash')
            && userStore.id
            && joinState?.bets?.length
        );
        if (!firstCheck) return;
        // ↓ 找到当前玩家的项
        const oSelf = joinState.bets.find((cur) => {
            return cur.user._id == userStore.id;
        });
        if (!oSelf) return; 
        return {
            ...oSelf,
            // ↓ 标记下注的币种与当前币种否一致，以备后用 
            isActiveCurrency: oSelf.currency === settingStore.pickedCurrency,
        };
    });

    // ▼ 上局(页面刷新时尚未完结的局) 游戏下注金额
    const lastRoundAmount = computed(() => {
        let {gameName='', lastRound} = joinState;
        lastRound ||= {};
        if (oCrashLastRound.value) {
            if (oCrashLastRound.value.stopped > 0){ // 说明已经提现
                return 0; // 返回0，那表示：不要用这个值，去使用普通的默认值 
            }
            return oCrashLastRound.value.amount;
        } else if (gameName.includes('blackjack') && lastRound.initialBetAmount){ 
            return lastRound.initialBetAmount;
        }
        return lastRound.amount || 0;
    });
    // ▼ 金额 * 2
    function toDouble() {
        if (nowBlance.value == 0){
            oForm.amount = 0;
        } else if (oForm.amount) {
            oForm.amount = Math.min(oForm.amount * 2, maxFinalValue.value);
        } else {
            // 处理金额为0或空的场景
            oForm.amount = minFinalValue.value;
        }
    }
    // ▼ 金额 / 2
    function toHalf() {
        let newValue = 0
        if (nowBlance.value > 0){
            newValue = Math.max(oForm.amount / 2, minFinalValue.value);
        }
        oForm.amount = newValue;
    }
    // ▼ 最“大”金额
    function toMax() {
        let newValue = 0
        if (nowBlance.value > 0){
            newValue = maxFinalValue.value;
        }
        oForm.amount = newValue;
    }

    // ▼监听币种切换，并设定默认值
    watch(
        [
            () => userStore.isLoggedIn, // 登录了
            () => joinState.hasGameInfoLoaded, // 游戏配置信息加载了
            () => settingStore.pickedCurrency, // 切换币种了
            () => lastRoundAmount.value, // 这是进行中的游戏的下注金额（页面刷新后此值将变化）
        ],
        (aNewValue) => {
            // 👇 未登录 || 游戏下注金额限制规则尚未加载
            if (!aNewValue[0] || !aNewValue[1]) {
                return;
            }
            let newAmount = 0;
            // ↓ 特殊游戏，默认值为0
            if (getGameName() === 'rouletteGame') {
                newAmount = 0;
            }else if (aNewValue[3] > 0){
                // ↓ 这是进行中的游戏的下注金额（“进行中的游戏” 即：某些游戏玩到一半时如果刷新浏览器，刷新后将加载这局未完成的游戏）
                newAmount = aNewValue[3];
            }else {
                // ↓ 用户无余额，或余额不足都将用此值做为默认值，这是 2024-06-28 商定的方案
                newAmount = betHelper.getCurrentDefaultBetAmount();
            }
            if (isDev || 1){
                console.log("设定下注金额：", newAmount); 
                console.log("监控信息：", deepCopy(aNewValue)); 
                console.log("余额信息：", deepCopy(nowBlance.value)); 
            }
            // ↓ 只保留2位，去除多余部分，防止 0.002 元显示为 0.00 元导致用户困惑  
            // ↓ 也防止 1.999 被转为 2.00 

            const amountMemory = useAmountMemoryStore();           
            newAmount = amountMemory.getLastAmount() || newAmount;         
            newAmount = Number(getFixed(newAmount, 2));
            oForm.amount = newAmount;
        },
        { immediate: true },
    );
    watch(
        () => oForm.isSubmiting,
        (aNewValue) => {
            joinState.gameBetFinished = aNewValue;
        },
    );
    // ↓ 在 Mini 版中这几个方法已经没有被调用了
    // ↓ 这个方法的主要作用是利用上方的 watch() 给游戏设定默认下注金额 
    return {
        toDouble,
        toHalf,
        toMax,
    };
}
